/* global Excel, Office */

import { t } from "./i18n";
import { ExcelExportContext, PdfExportOptions } from "./types";

interface WorksheetRestore {
  id: string;
  name: string;
  visibility?: "Visible";
  zoom?: Excel.PageLayoutZoomOptions;
}

// Keep unsuccessful restorations available for an explicit retry in this task pane.
let pendingRestorations: WorksheetRestore[] = [];
let exporting = false;

export function supportsExcelApi(version: string): boolean {
  return Office.context.requirements?.isSetSupported("ExcelApi", version) === true;
}

function isExcelHost(): boolean {
  return Office.context.host === Office.HostType.Excel && typeof Excel !== "undefined";
}

export async function readExcelExportContext(): Promise<ExcelExportContext | null> {
  if (!isExcelHost() || !supportsExcelApi("1.2")) {
    return null;
  }

  return Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    const active = worksheets.getActiveWorksheet();
    worksheets.load("items/id,items/name,items/visibility");
    active.load("id,name");
    if (supportsExcelApi("1.7")) {
      context.workbook.protection.load("protected");
    }
    await context.sync();
    return {
      activeWorksheetId: active.id,
      activeWorksheetName: active.name,
      visibleWorksheets: worksheets.items
        .filter((sheet) => sheet.visibility === "Visible")
        .map((sheet) => ({ id: sheet.id, name: sheet.name })),
      structureProtected: supportsExcelApi("1.7") && context.workbook.protection.protected,
    };
  });
}

export function getPendingRestorationNames(): string[] {
  return pendingRestorations.map((sheet) => sheet.name);
}

export async function watchExcelDocument(onChange: () => void): Promise<void> {
  if (!isExcelHost() || !supportsExcelApi("1.9")) {
    return;
  }
  try {
    await Excel.run(async (context) => {
      const handler = async () => onChange();
      context.workbook.worksheets.onActivated.add(handler);
      context.workbook.worksheets.onChanged.add(handler);
      context.workbook.worksheets.onAdded.add(handler);
      context.workbook.worksheets.onDeleted.add(handler);
      await context.sync();
    });
  } catch (_error) {
    // The prepared attachment is explicitly a snapshot; manual refresh remains available.
  }
}

export async function retryExcelRestoration(): Promise<void> {
  const failures: WorksheetRestore[] = [];
  // A missing or protected sheet must not stop restoration of the remaining sheets.
  for (const saved of pendingRestorations) {
    const failed: WorksheetRestore = { id: saved.id, name: saved.name };
    if (saved.visibility && !(await restoreWorksheetSetting(saved, "visibility"))) {
      failed.visibility = saved.visibility;
    }
    if (saved.zoom && !(await restoreWorksheetSetting(saved, "zoom"))) {
      failed.zoom = saved.zoom;
    }
    if (failed.visibility || failed.zoom) {
      failures.push(failed);
    }
  }
  pendingRestorations = failures;
  if (failures.length) {
    throw new Error(`${t("excelRestoreFailure")} ${getPendingRestorationNames().join(", ")}`);
  }
}

async function restoreWorksheetSetting(saved: WorksheetRestore, setting: "visibility" | "zoom"): Promise<boolean> {
  try {
    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets.load("items/id");
      await context.sync();
      const sheet = sheets.items.find((item) => item.id === saved.id);
      if (sheet) {
        if (setting === "visibility") {
          sheet.visibility = saved.visibility;
        } else {
          sheet.pageLayout.zoom = saved.zoom;
        }
        await context.sync();
      }
    });
    return true;
  } catch (_error) {
    return false;
  }
}

export async function exportCurrentDocumentAsPdf(maxBytes: number, options: PdfExportOptions = {}): Promise<Blob> {
  if (exporting) {
    throw new Error(t("preparingDocument"));
  }
  if (pendingRestorations.length) {
    throw new Error(t("excelRestoreFailure"));
  }
  exporting = true;
  let file: Office.File | null = null;
  let pdf: Blob;
  let exportError: unknown;
  let restoreError: unknown;

  try {
    await prepareExcelPdfExport(options);
    file = await getFile(Office.FileType.Pdf);
    if (file.size > maxBytes) {
      throw new Error(t("documentTooLarge"));
    }
    pdf = new Blob(await getAllSlices(file), { type: "application/pdf" });
  } catch (error) {
    exportError = error;
  } finally {
    try {
      if (file) {
        await closeFile(file);
      }
    } finally {
      try {
        await retryExcelRestoration();
      } catch (error) {
        restoreError = error;
      }
      exporting = false;
    }
  }

  if (restoreError) {
    const originalMessage = exportError instanceof Error ? `${exportError.message} ` : "";
    throw new Error(`${originalMessage}${(restoreError as Error).message}`);
  }
  if (exportError) {
    throw exportError;
  }
  return pdf;
}

async function prepareExcelPdfExport(options: PdfExportOptions): Promise<void> {
  if (!isExcelHost()) {
    return;
  }
  const excel = options.excel || { scope: "active-sheet", pageLayout: "preserve" };
  if (excel.scope === "active-sheet" && !supportsExcelApi("1.2")) {
    throw new Error(t("excelScopeUnsupported"));
  }
  if (excel.pageLayout !== "preserve" && !supportsExcelApi("1.9")) {
    throw new Error(t("excelLayoutUnsupported"));
  }
  if (excel.scope === "workbook" && excel.pageLayout === "preserve") {
    return;
  }

  const target = excel.context || (await readExcelExportContext());
  if (!target) {
    throw new Error(t("excelExportSettingsFailure"));
  }
  if (excel.scope === "active-sheet" && target.structureProtected && target.visibleWorksheets.length > 1) {
    throw new Error(t("excelStructureProtected"));
  }

  try {
    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      const currentSheets = sheets.load("items/id,items/name,items/visibility");
      await context.sync();
      const visibleSheets = currentSheets.items.filter((sheet) => sheet.visibility === "Visible");
      if (excel.scope === "active-sheet" && !visibleSheets.some((sheet) => sheet.id === target.activeWorksheetId)) {
        throw new Error(t("excelTargetUnavailable"));
      }
      const layoutSheets =
        excel.pageLayout === "preserve"
          ? []
          : visibleSheets.filter((sheet) => excel.scope === "workbook" || sheet.id === target.activeWorksheetId);
      layoutSheets.forEach((sheet) => sheet.load("pageLayout/zoom"));
      if (layoutSheets.length) {
        await context.sync();
      }
      // Capture every original value before queuing the first mutation.
      pendingRestorations = visibleSheets
        .map(
          (sheet): WorksheetRestore => ({
            id: sheet.id,
            name: sheet.name,
            visibility: excel.scope === "active-sheet" && sheet.id !== target.activeWorksheetId ? "Visible" : undefined,
            zoom: layoutSheets.some((layout) => layout.id === sheet.id) ? { ...sheet.pageLayout.zoom } : undefined,
          })
        )
        .filter((saved) => saved.visibility || saved.zoom);

      pendingRestorations.forEach((saved) => {
        const sheet = sheets.getItem(saved.id);
        if (saved.visibility) {
          sheet.visibility = "Hidden";
        }
        if (saved.zoom) {
          // Zero removes the height limit; orientation and print areas remain untouched.
          sheet.pageLayout.zoom = {
            horizontalFitToPages: 1,
            verticalFitToPages: excel.pageLayout === "fit-columns" ? 0 : 1,
          };
        }
      });
      await context.sync();
    });
  } catch (error) {
    if (error instanceof Error && error.message === t("excelTargetUnavailable")) {
      throw error;
    }
    throw new Error(t("excelExportSettingsFailure"));
  }
}

function closeFile(file: Office.File): Promise<void> {
  return new Promise((resolve) => {
    file.closeAsync(() => resolve());
  });
}

function getFile(fileType: Office.FileType): Promise<Office.File> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(fileType, { sliceSize: 1024 * 1024 }, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
        return;
      }

      reject(new Error(result.error?.message || t("prepareDocumentFailure")));
    });
  });
}

async function getAllSlices(file: Office.File): Promise<ArrayBuffer[]> {
  const slices: ArrayBuffer[] = [];

  for (let index = 0; index < file.sliceCount; index += 1) {
    const slice = await getSlice(file, index);
    const bytes = new Uint8Array(slice.data as number[]);
    const buffer = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(buffer).set(bytes);
    slices.push(buffer);
  }

  return slices;
}

function getSlice(file: Office.File, index: number): Promise<Office.Slice> {
  return new Promise((resolve, reject) => {
    file.getSliceAsync(index, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
        return;
      }

      reject(new Error(result.error?.message || t("prepareDocumentFailure")));
    });
  });
}
